import React from "react";
import { createPortal } from "react-dom";
import { CameraIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";

/** Same mobile-friendly panel as DocumentManagerV6 upload dialog. */
export const DOCUMENT_UPLOAD_PREVIEW_PANEL =
  "!h-[100dvh] !max-h-[100dvh] !min-h-[100dvh] max-sm:w-full max-sm:max-w-full max-sm:rounded-none sm:!h-[720px] sm:!min-h-0 sm:!max-h-[80vh] sm:rounded-2xl sm:max-w-2xl md:max-w-2xl lg:max-w-2xl";

/** Above SaveBar (2200) and default PMDialog (2000); below confirm modals (3600). */
export const DOCUMENT_UPLOAD_PREVIEW_Z_INDEX = 2300;

/** Nested selectors / child dialogs inside upload preview (folder, drawing, position). */
export const DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX =
  DOCUMENT_UPLOAD_PREVIEW_Z_INDEX + 100;

/**
 * Classic document upload preview (stacked image + title per file) used across the app.
 */
export function DocumentUploadPreviewDialog({
  open,
  onClose,
  title,
  filesToUpload = [],
  onTitleChange,
  onDeleteFile,
  onAddMore,
  onConfirm,
  isUploading = false,
  confirmDisabled = false,
  zIndex = DOCUMENT_UPLOAD_PREVIEW_Z_INDEX,
  supportPdf = true,
  renderBeforeList,
  renderAfterList,
  renderFileExtra,
}) {
  const { t } = useTranslation();
  const dialogTitle = title ?? t("documentUpload.preview.title");

  const dialog = (
    <PMDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      dense
      zIndex={zIndex}
      panelClassName={DOCUMENT_UPLOAD_PREVIEW_PANEL}
    >
      {filesToUpload.length > 0 && (
        <div className="flex min-h-0 flex-1 flex-col px-1 sm:px-0">
          {renderBeforeList?.()}

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain sm:max-h-96">
            {filesToUpload.map((file, index) => (
              <div
                key={`${file.file?.name || file.title}-${index}`}
                className="relative flex flex-col gap-3 rounded-xl bg-gray-50 px-4 py-4 ring-1 ring-inset ring-gray-200/90"
              >
                <button
                  type="button"
                  onClick={() => onDeleteFile(index)}
                  disabled={isUploading}
                  className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white shadow-sm transition-colors hover:bg-red-600 disabled:opacity-50"
                  title={t("documentUpload.preview.removeFile")}
                  aria-label={t("documentUpload.preview.removeFile")}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>

                <img
                  src={
                    supportPdf && file.file?.type === "application/pdf"
                      ? PdfPlaceholder
                      : file.previewUrl
                  }
                  className="w-full max-h-[45vh] rounded-lg object-contain sm:h-44 sm:max-h-none sm:object-cover"
                  alt={file.title || file.file?.name || ""}
                />

                <PMField
                  name={`upload-title-${index}`}
                  type="text"
                  label={t("Title")}
                  value={file.title}
                  disabled={isUploading}
                  onChange={(e) => onTitleChange(index, e.target.value)}
                />

                {renderFileExtra?.(file, index)}
              </div>
            ))}
          </div>

          {renderAfterList?.()}

          <div className="mt-auto flex-shrink-0 border-t border-gray-200 bg-white pt-3 pb-[max(calc(var(--mobile-nav-height,0px)+0.75rem),env(safe-area-inset-bottom))] sm:border-0 lg:pb-0">
            {!isUploading && (
              <button
                type="button"
                className="mb-3 flex w-full min-h-[44px] items-center justify-center rounded-lg bg-emerald-600 p-3 text-white transition-colors hover:bg-emerald-700 sm:p-2.5"
                onClick={onAddMore}
              >
                <CameraIcon className="mr-2 h-5 w-5" aria-hidden />
                {t("documentUpload.preview.addMore")}
              </button>
            )}

            <div className="flex justify-between gap-3">
              <button
                type="button"
                className="min-h-[44px] flex-1 rounded-lg bg-slate-600 p-3 text-white transition-colors hover:bg-slate-700 sm:w-24 sm:flex-none sm:p-2.5"
                onClick={onClose}
                disabled={isUploading}
              >
                {t("documentUpload.preview.close")}
              </button>
              <button
                type="button"
                disabled={isUploading || confirmDisabled || !filesToUpload.length}
                className="min-h-[44px] flex-1 rounded-lg bg-emerald-600 p-3 font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400 sm:w-28 sm:flex-none sm:p-2.5"
                onClick={onConfirm}
              >
                {isUploading
                  ? t("documentUpload.preview.uploading")
                  : t("documentUpload.preview.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </PMDialog>
  );

  if (typeof document === "undefined") {
    return dialog;
  }

  return createPortal(dialog, document.body);
}
